import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ReactComponent as InformationIcon } from "../static/assets/information.svg";

const TOOLTIP_GAP = 8;

function InfoTipIcon({ icon, nudgeY }) {
  if (!icon) {
    return (
      <InformationIcon
        className="text-gray-500 relative leading-none w-[13px] h-[13px]"
        style={{ top: nudgeY }}
        aria-hidden
      />
    );
  }

  if (isValidElement(icon)) {
    return cloneElement(icon, {
      className: ["relative leading-none", icon.props.className]
        .filter(Boolean)
        .join(" "),
      style: { ...(icon.props.style || {}), top: nudgeY },
      "aria-hidden": true,
    });
  }

  const Icon = icon;
  return (
    <Icon
      className="relative leading-none w-[14px] h-[14px]"
      style={{ color: "#5E5E5E", top: nudgeY }}
      aria-hidden
    />
  );
}

export default function InfoTip({
  children,
  icon = null,
  widthMaxRem = 18,
  offset = 8,
  nudgeY = -1,
}) {
  const hostRef = useRef(null);
  const tipRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [tipStyle, setTipStyle] = useState(null);

  useEffect(() => {
    if (!open) {
      setTipStyle(null);
      return;
    }

    const update = () => {
      const host = hostRef.current;
      const tip = tipRef.current;
      if (!host || !tip) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      tip.style.visibility = "hidden";
      tip.style.opacity = "0";

      const hostRect = host.getBoundingClientRect();
      const tipRect = tip.getBoundingClientRect();
      const tipW = Math.min(tipRect.width || 0, widthMaxRem * 16);
      const tipH = tipRect.height || 0;

      let left;
      const half = tipW / 2;
      if (hostRect.left < half + offset) {
        left = hostRect.left;
      } else if (vw - hostRect.right < half + offset) {
        left = hostRect.right - tipW;
      } else {
        left = hostRect.left + hostRect.width / 2 - tipW / 2;
      }

      const spaceTop = hostRect.top;
      const spaceBottom = vh - hostRect.bottom;
      const showAbove =
        spaceTop >= tipH + TOOLTIP_GAP || spaceBottom <= spaceTop;

      const top = showAbove
        ? hostRect.top - tipH - TOOLTIP_GAP
        : hostRect.bottom + TOOLTIP_GAP;

      tip.style.visibility = "";
      tip.style.opacity = "";
      setTipStyle({
        position: "fixed",
        top,
        left,
        maxWidth: `${widthMaxRem}rem`,
        opacity: 1,
        zIndex: 9999,
      });
    };

    const t = requestAnimationFrame(() => update());
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    return () => {
      cancelAnimationFrame(t);
      ro.disconnect();
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [open, widthMaxRem, offset]);

  const tooltipContent = open ? (
    <span
      ref={tipRef}
      className="pointer-events-none w-max max-w-[18rem] rounded-md bg-black/85 px-2 py-1 text-[13px] leading-snug text-white shadow-lg transition-opacity duration-150"
      style={
        tipStyle ?? {
          position: "fixed",
          left: -9999,
          top: 0,
          opacity: 0,
        }
      }
      role="tooltip"
    >
      {children}
    </span>
  ) : null;

  return (
    <>
      <span
        ref={hostRef}
        className="relative inline-flex shrink-0 items-center self-center cursor-help ml-1 leading-none"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        tabIndex={0}
        aria-label="More info"
      >
        <InfoTipIcon icon={icon} nudgeY={nudgeY} />
      </span>
      {createPortal(tooltipContent, document.body)}
    </>
  );
}
